
import React from 'react';
import {
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';

interface ReviewStepProps {
  customerNumber: string;
  businessName: string;
  vehicleVIN: string;
  vehicleMileage: string;
  licenseNumber: string;
  deviceData: {
    taximeterVendor: string;
    taximeterType: string;
    taximeterSerial: string;
    taximeterYearOfProduction: string;
    deviceVendor: string;
    deviceType: string;
    deviceSerial: string;
    deviceYearOfProduction: string;
  };
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  customerNumber,
  businessName,
  vehicleVIN,
  vehicleMileage,
  licenseNumber,
  deviceData
}) => {
  const { t } = useTranslation();

  const getVendorDisplayName = (vendor: string) => {
    const vendorMap: { [key: string]: string } = {
      'hale': 'Hale',
      'semitron': 'Semitron',
      'digitax': 'Digitax',
      'taxifusion': 'TaxiFusion'
    };
    return vendorMap[vendor] || vendor;
  };

  const getDeviceTypeName = (type: string) => {
    const deviceTypeMap: { [key: string]: string } = {
      '4': 'SEI-03 Box',
      '9': 'MCT07',
      'TBOX': 'TBOX'
    };
    return deviceTypeMap[type] || type;
  };

  const needsSigningDevice = () => {
    if (!deviceData.taximeterVendor) return false;
    if (deviceData.taximeterVendor === 'semitron') return false;
    if (deviceData.taximeterVendor === 'hale' &&
        (deviceData.taximeterType === 'MCT07' || deviceData.taximeterType === 'WSZ07')) {
      return false;
    }
    return true;
  };

  return (
    <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Grid container spacing={2}>
        {/* Customer Information Section */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" fontWeight="bold">{t('assistant.reviewStep.customer')}</Typography>
          <Divider sx={{ my: 1 }} />
          <Grid container>
            <Grid size={{ xs: 12, sm:6, md: 4 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.reviewStep.customerNumber')}
                    secondary={customerNumber}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 8 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.reviewStep.businessName')}
                    secondary={businessName}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Grid>

        {/* Vehicle Information Section */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" fontWeight="bold">{t('assistant.reviewStep.vehicle')}</Typography>
          <Divider sx={{ my: 1 }} />
          <Grid container>
            <Grid size={{ xs: 12, sm:6, md: 4 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.reviewStep.vin')}
                    secondary={vehicleVIN}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.vehicleIdentificationStep.licenseNumber')}
                    secondary={licenseNumber}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.reviewStep.mileage')}
                    secondary={vehicleMileage === '' ? t('assistant.reviewStep.notApplicable') : `${vehicleMileage} km`}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Grid>

        {/* Device Information Section */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" fontWeight="bold">{t('assistant.reviewStep.device')}</Typography>
          <Divider sx={{ my: 1 }} />

          {/* Taximeter Information */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
            Taxameter
          </Typography>
          <Grid container>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.deviceStep.taximeterVendor')}
                    secondary={getVendorDisplayName(deviceData.taximeterVendor)}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.deviceStep.taximeterType')}
                    secondary={deviceData.taximeterType}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.deviceStep.taximeterSerial')}
                    secondary={deviceData.taximeterSerial}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('assistant.deviceStep.taximeterYearOfProduction')}
                    secondary={deviceData.taximeterYearOfProduction}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body1', color: 'text.primary' }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          {/* Signing Device Information - Only show if present */}
          {needsSigningDevice() && deviceData.deviceVendor && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 1 }}>
                Signiereinheit
              </Typography>
              <Grid container>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <List dense disablePadding>
                    <ListItem disableGutters>
                      <ListItemText
                        primary={t('assistant.deviceStep.deviceVendor')}
                        secondary={getVendorDisplayName(deviceData.deviceVendor)}
                        slotProps={{
                          primary: { variant: 'caption', color: 'text.secondary' },
                          secondary: { variant: 'body1', color: 'text.primary' }
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <List dense disablePadding>
                    <ListItem disableGutters>
                      <ListItemText
                        primary={t('assistant.deviceStep.deviceType')}
                        secondary={getDeviceTypeName(deviceData.deviceType)}
                        slotProps={{
                          primary: { variant: 'caption', color: 'text.secondary' },
                          secondary: { variant: 'body1', color: 'text.primary' }
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <List dense disablePadding>
                    <ListItem disableGutters>
                      <ListItemText
                        primary={t('assistant.deviceStep.deviceSerial')}
                        secondary={deviceData.deviceSerial}
                        slotProps={{
                          primary: { variant: 'caption', color: 'text.secondary' },
                          secondary: { variant: 'body1', color: 'text.primary' }
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <List dense disablePadding>
                    <ListItem disableGutters>
                      <ListItemText
                        primary={t('assistant.deviceStep.deviceYearOfProduction')}
                        secondary={deviceData.deviceYearOfProduction || new Date().getFullYear()}
                        slotProps={{
                          primary: { variant: 'caption', color: 'text.secondary' },
                          secondary: { variant: 'body1', color: 'text.primary' }
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>

        {/* Hale Device Alert */}
        {(deviceData.taximeterVendor === 'hale' || deviceData.deviceVendor === 'hale') && (
          <Grid size={{ xs: 12 }}>
            <Alert
              severity="info"
              sx={{ mt: 2 }}
            >
              <Typography
                variant="body2"
                dangerouslySetInnerHTML={{
                  __html: t('assistant.reviewStep.haleDeviceAlert')
                }}
              />
            </Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  )};

export default ReviewStep;
